import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { shippingService, firmService } from "../services/dataService";
import { Firm, ShipmentListItem, ShipmentStatus, ShippingStats } from "../types";
import { turkishIncludes } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import FirmPickerField from "../components/FirmPicker";
import "./Receivables.css";
import "./Purchasing.css";
import "./Shipping.css";

export const SH_STATUS_LABEL: Record<ShipmentStatus, string> = {
  [ShipmentStatus.Draft]: "Taslak",
  [ShipmentStatus.Shipped]: "Sevk Edildi",
  [ShipmentStatus.Delivered]: "Teslim Edildi",
  [ShipmentStatus.Cancelled]: "İptal",
};

export const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");
export const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
export const fmtQty = (n: number) => n.toLocaleString("tr-TR", { maximumFractionDigits: 3 });

type StatusFilter = "" | "open" | ShipmentStatus;

const Shipping: React.FC = () => {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const [stats, setStats] = useState<ShippingStats | null>(null);
  const [rows, setRows] = useState<ShipmentListItem[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("open");
  const [firmId, setFirmId] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [s, list] = await Promise.all([shippingService.stats(), shippingService.shipments()]);
      setStats(s); setRows(list);
    } catch (e: any) { setError(e?.response?.data?.message || e?.response?.data || "Sevkiyatlar yüklenemedi."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { firmService.getFirms().then(setFirms).catch(() => setFirms([])); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (status === "open" && (r.status === ShipmentStatus.Delivered || r.status === ShipmentStatus.Cancelled)) return false;
    if (status !== "" && status !== "open" && r.status !== status) return false;
    if (firmId && r.firmId !== firmId) return false;
    if (search.trim()) {
      const hay = `${r.number} ${r.firmName} ${r.waybillNo || ""} ${r.vehiclePlate || ""} ${r.models || ""} ${r.carrier || ""}`;
      if (!turkishIncludes(hay, search.trim())) return false;
    }
    return true;
  }), [rows, status, firmId, search]);

  if (loading && !stats) return <PageLoader message="Sevkiyatlar yükleniyor..." />;

  return (
    <div className="rc-page">
      <div className="rc-head">
        <div>
          <h1>Sevkiyat</h1>
          <p>Sevk irsaliyesi, araç ve şoför bilgisi, teslimat onayı. Sevk stoktan otomatik düşer.</p>
        </div>
        <div className="rc-head-actions">
          {has("shipping:create") && (
            <button className="rc-btn primary" onClick={() => navigate("/shipping/new")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              Yeni Sevkiyat
            </button>
          )}
          <button className="rc-btn" onClick={load} disabled={loading}>Yenile</button>
        </div>
      </div>

      {error && <div className="ic-alert error" style={{ marginBottom: "1rem" }}>{String(error)}</div>}

      {stats && (
        <div className="rc-kpis">
          <div className="rc-kpi"><div className="rc-kpi-label">Hazırlanan</div><div className="rc-kpi-value">{stats.draftCount}</div><div className="rc-kpi-sub">Taslak sevkiyat</div></div>
          <div className="rc-kpi"><div className="rc-kpi-label">Yolda</div><div className="rc-kpi-value">{stats.inTransitCount}</div><div className="rc-kpi-sub">{stats.inTransitPackages} koli teslim bekliyor</div></div>
          <div className="rc-kpi collected"><div className="rc-kpi-label">Son 30 Gün Teslim</div><div className="rc-kpi-value">{stats.deliveredLast30}</div><div className="rc-kpi-sub">Teslim onaylanan sevkiyat</div></div>
          <div className={`rc-kpi ${stats.awaitingOrders > 0 ? "overdue" : ""}`}><div className="rc-kpi-label">Sevk Bekleyen Sipariş</div><div className="rc-kpi-value">{stats.awaitingOrders}</div><div className="rc-kpi-sub">Tamamlanmış, tam sevk edilmemiş</div></div>
        </div>
      )}

      <div className="rc-toolbar">
        <input className="rc-input rc-search" placeholder="Sevkiyat no, irsaliye no, firma, plaka veya model ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <FirmPickerField variant="toolbar" value={firmId} firms={firms} nullLabel="Tüm firmalar" allowCreate={false} title="Firmaya göre süz" onChange={(f) => setFirmId(f?.firmId || "")} />
        <select className="rc-input" value={status} onChange={(e) => setStatus(e.target.value === "" || e.target.value === "open" ? (e.target.value as "" | "open") : (parseInt(e.target.value, 10) as ShipmentStatus))}>
          <option value="open">Açık sevkiyatlar</option>
          <option value="">Tümü</option>
          {Object.entries(SH_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="rc-card"><div className="rc-table-wrap">
        <table className="rc-table rt rt-stack">
          <thead><tr>
            <th>Sevkiyat</th><th>Firma</th><th className="rt-p3">Tarih</th><th>Durum</th>
            <th className="rt-p2">Araç / Nakliyeci</th><th className="rt-p3">İrsaliye No</th>
            <th className="num">Miktar</th><th className="num rt-p3">Koli</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8}><div className="rc-empty"><strong>{rows.length ? "Filtreye uyan sevkiyat yok" : "Henüz sevkiyat yok"}</strong>{!rows.length && "İlk sevkiyatı \"Yeni Sevkiyat\" ile oluşturun."}</div></td></tr>
            ) : filtered.map((r) => (
              <tr key={r.shipmentId} className="clickable" onClick={() => navigate(`/shipping/${r.shipmentId}`)}>
                <td className="rt-main" data-label="Sevkiyat"><div className="sh-route"><b>{r.number}</b><span>{r.models || `${r.lineCount} sipariş`}</span></div></td>
                <td data-label="Firma"><div className="rc-firm"><span className="rc-firm-name">{r.firmName}</span>{r.firmCode && <span className="rc-firm-code">{r.firmCode}</span>}</div></td>
                <td className="rt-p3" data-label="Tarih">{fmtDate(r.shippedAt || r.shipmentDate)}</td>
                <td data-label="Durum">
                  <span className={`sh-status s${r.status}`}>{SH_STATUS_LABEL[r.status]}</span>
                  {r.status === ShipmentStatus.Delivered && r.receivedBy && <div className="pu-stock-hint">{r.receivedBy} · {fmtDate(r.deliveredAt)}</div>}
                </td>
                <td className="rt-p2" data-label="Araç">
                  {r.vehiclePlate ? <span className="sh-plate">{r.vehiclePlate}</span> : null}
                  {r.carrier ? <span className="pu-stock-hint" style={{ marginLeft: r.vehiclePlate ? "0.4rem" : 0 }}>{r.carrier}</span> : (!r.vehiclePlate ? "—" : null)}
                </td>
                <td className="rt-p3 mono" data-label="İrsaliye">{r.waybillNo || "—"}</td>
                <td className="num" data-label="Miktar">{fmtQty(r.totalQuantity)}</td>
                <td className="num rt-p3" data-label="Koli">{r.packageCount ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>
    </div>
  );
};

export default Shipping;
